import moment from 'moment'
import queryString from 'query-string'
import React, { useState } from 'react'
import { Button, Card } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import { useHistory, useLocation } from 'react-router-dom'

import useCallApi from '../../../hooks/useCallApi'
import useDateRangeFilter from '../../../hooks/useDateRangeFilter'
import numberSort from '../../../helpers/numberSort'
import DateRangeFilter from '../../Reports/Filters/DateRangeFilter/DateRangeFilter'
import Advertiser from '../../../templates/Advertiser/Advertiser'
import Campaign from '../../../templates/Campaign/Campaign'
import Dollars from '../../../templates/Dollars/Dollars'
import Number from '../../../templates/Number/Number'
import ReportTable from '../../../templates/ReportTable/ReportTable'
import Loading from '../../../templates/UI/Loading/Loading'

const NewlyLaunchedCampaignRevenueReport = ({ showSystemMessage }) => {
    document.title = 'Newly Launched Campaign Revenue'

    const history = useHistory()
    const location = useLocation()
    const urlParams = queryString.parse(location.search)

    const { selectedStartDate, selectedEndDate, quickDate, handleChangeStart, handleChangeEnd, handleQuickDate } = useDateRangeFilter()
    const [launchedAfter, setLaunchedAfter] = useState(urlParams.launchedAfter || moment().startOf('year').format('YYYY-MM-DD'))

    const { data, dataLoaded, refetch } = useCallApi(`reports/custom/newCampaignRevenue?launchedAfter=${launchedAfter}&startDate=${selectedStartDate}&endDate=${selectedEndDate}`, 'GET', {}, [], showSystemMessage)

    const refreshReport = () => {
        refetch()
        history.push(`/reports/custom/newCampaignRevenue?launchedAfter=${launchedAfter}&startDate=${selectedStartDate}&endDate=${selectedEndDate}`)
    }

    const getReportOutput = () => {
        const columns = [
            {
                Header: 'ID',
                accessor: 'campaign_id',
                Cell: ({ cell: { value } }) => {
                    return <>{value}</>
                },
            },
            {
                Header: 'Campaign',
                accessor: 'campaign_name',
                Cell: (tableData) => {
                    return <Campaign oid={tableData.data[tableData.row.index].campaign_id} name={tableData.data[tableData.row.index].campaign_name} status={tableData.data[tableData.row.index].campaign_status} />
                },
            },
            {
                Header: 'Launched',
                accessor: 'launch_date',
                Cell: ({ cell: { value } }) => {
                    return <>{moment(value).utc().format('YYYY-MM-DD')}</>
                },
            },
            {
                Header: 'Advertiser',
                accessor: 'advertiser_name',
                Cell: (tableData) => {
                    return <Advertiser id={tableData.data[tableData.row.index].advertiser_id} name={tableData.data[tableData.row.index].advertiser_name} />
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Clicks</div>,
                sortType: numberSort,
                accessor: 'clicks',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <>{Intl.NumberFormat('en-GB').format(value)}</>
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Conversions</div>,
                sortType: numberSort,
                accessor: 'conversions',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <>{Intl.NumberFormat('en-GB').format(value)}</>
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Net Earnings</div>,
                sortType: numberSort,
                accessor: 'network_earnings',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Aff Earnings</div>,
                sortType: numberSort,
                accessor: 'affiliate_earnings',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Margin</div>,
                sortType: numberSort,
                accessor: 'margin',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Margin %</div>,
                accessor: 'margin_percentage',
                className: 'text-right',
                Cell: (tableData) => {
                    const { margin, network_earnings } = tableData.data[tableData.row.index]
                    return (
                        <>
                            <Number value={network_earnings > 0 ? (margin / network_earnings) * 100 : 0} decimals={2} />%
                        </>
                    )
                },
            },
        ]

        return <ReportTable data={data.report} columns={columns} totals={data.totals} />
    }

    return (
        <div>
            <div className="page-header d-flex align-items-center">
                <span className="mb-h1">Reports</span>
            </div>
            <Card>
                <div className="d-flex align-items-center p-3">
                    <div className="mr-4 d-flex align-items-center">
                        <h1 className="mb-h1-dark">Newly Launched Campaign Revenue</h1>
                    </div>
                    <span className="text-small mr-2">Campaigns launched after date:</span>
                    <div className="mr-3">
                        <DatePicker selectsStart placeholderText={'Choose a date'} className="form-control form-control-sm" selected={launchedAfter ? moment(launchedAfter).toDate() : null} onChange={(date) => setLaunchedAfter(moment(date).format('YYYY-MM-DD'))} />
                    </div>
                    <DateRangeFilter quickDate={quickDate} selectedStartDate={selectedStartDate} selectedEndDate={selectedEndDate} handleChangeStart={handleChangeStart} handleChangeEnd={handleChangeEnd} handleQuickDate={handleQuickDate} />
                    <Button size="sm" onClick={refreshReport}>
                        Refresh Report
                    </Button>
                </div>
                <div className="alert alert-info mx-2">Owned and Operated (O&O) [e.g. benefits-authority.com], costs have been attributed to the campaigns that are linked out from the funnel. In order to prevent double counting these costs, the O&O campaigns themselves are hidden on this report.</div>
                <hr className="m-0" />
                {dataLoaded ? data.report.length === 0 ? <div className="p-3">No campaigns for the current parameters.</div> : getReportOutput() : <Loading />}
            </Card>
        </div>
    )
}

export default NewlyLaunchedCampaignRevenueReport
